/**
 * The customer's own colours for the chart elements the Configuration dialog exposes.
 *
 * <h2>Stored per browser, like every other view preference here</h2>
 *
 * Same shape as `toolbarBarMode.js`: a localStorage key, a same-tab event because `storage` only
 * reaches OTHER tabs, and a read that treats anything unrecognised as unset. Deliberately the same
 * shape rather than a new one — two mechanisms for "a preference this browser remembers" is one
 * more than anybody can keep in their head.
 *
 * <h2>THIS MODULE HOLDS NO DEFAULT COLOURS, and that is the point</h2>
 *
 * Every default already exists somewhere that owns it: both halves of a PTA/PTB cycle take
 * `cycleColors`, the arrows `UP_COLOR` and `DOWN_COLOR`, the RSI line `rsiLineDefault`. Copying
 * those hex values here would make this another place a colour is written down, and the colour
 * would then be whatever the last editor happened to change — which is exactly how `#d4a017`
 * came to be defined in three files, each claiming to share it with the others.
 *
 * <p>The cycles took theirs from the PLANET each follows until 2026-10-16, which is how PTB came
 * to be drawn in PTA's amber: the planet table is shared with the retrograde study, where
 * Mercury's amber means Mercury.
 *
 * <p>So {@link resolveColor} takes the fallback from the CALLER, which already has the real default
 * in hand. An unconfigured install renders byte-for-byte as before, because nothing here is
 * consulted until a customer picks something.
 */

/** The key, alongside `toolbar_bar_mode`. */
export const WORKSPACE_COLORS_KEY = 'workspace_colors';

/** Fired when the colours change in THIS tab. */
export const WORKSPACE_COLORS_CHANGED_EVENT = 'astrocharts:workspace-colors-changed';

/**
 * The roles, in the order the dialog shows them.
 *
 * <p>Grouped, because a reader configuring "PTA" thinks about one study with two halves rather than
 * about four independent swatches. `group` is what lets the dialog say that without hard-coding a
 * layout that then disagrees with this list.
 *
 * <p>The keys are the contract: they appear in stored JSON in a customer's browser, so renaming one
 * silently discards that customer's choice. Add rather than rename.
 */
export const WORKSPACE_COLOR_ROLES = [
  { key: 'ptaHorizontal', group: 'PTA', label: 'Horizontal bands', hint: 'the price range a cycle established' },
  { key: 'ptaVertical', group: 'PTA', label: 'Vertical bands', hint: 'the time window itself' },
  { key: 'ptbHorizontal', group: 'PTB', label: 'Horizontal bands', hint: 'the price range a cycle established' },
  { key: 'ptbVertical', group: 'PTB', label: 'Vertical bands', hint: 'the time window itself' },
  // THREE roles, because a CTS mark is now drawn in three states and the dialog must offer one
  // swatch per thing on screen -- no more and no fewer.
  //
  // They went to ONE on 2026-10-21, when every mark became a neutral amber tick at the foot of
  // the grid: two direction controls over one colour would have been two swatches that change
  // nothing, which is the "a control that lies" defect this codebase keeps recording. The rail
  // half of that is unchanged and `ctsMark` is now precisely its colour. What came back is the
  // other half: a mark drawn at a price on a REVEALED bar does claim a direction, read from price
  // action the reader can see, so it gets its colour back and its control with it.
  //
  // A preference stored under one of these while it was absent is simply read again --
  // `readWorkspaceColors` keys off this list, so nothing had to be migrated either way.
  { key: 'ctsUp', group: 'CTS', label: 'Up signal', hint: 'triangles on the chart where the signal reads up' },
  { key: 'ctsDown', group: 'CTS', label: 'Down signal', hint: 'triangles on the chart where the signal reads down' },
  { key: 'ctsMark', group: 'CTS', label: 'Unrevealed marks', hint: 'the neutral triangles at the foot of the chart' },
  // INDICATORS, a seventh role and a third group. The RSI line used to carry a literal at the
  // series, so "the colour from my palette" was not a thing it could obey; the dialog builds
  // itself from this list, so adding the key is the whole of exposing it.
  //
  // Unset falls back to the ACTIVE THEME's `rsiLine`, which differs between light and dark --
  // one stored hex would be unreadable on one of them, and a reader who has expressed no
  // preference should not be given one.
  { key: 'rsiLine', group: 'Indicators', label: 'RSI line', hint: 'the oscillator in the lower panel' },
];

export const WORKSPACE_COLOR_KEYS = WORKSPACE_COLOR_ROLES.map(r => r.key);

/**
 * Whether a string is a colour this can store.
 *
 * <p>`#rgb` or `#rrggbb` only. Narrow on purpose: the dialog's picker emits exactly this, and the
 * value goes straight into an ECharts option where a malformed string does not throw — it renders
 * black, or renders nothing, with no warning. A stored value is read back long after whoever typed
 * it has gone, so the check belongs at the boundary rather than at the point of use.
 */
export function isHexColor(value) {
  return typeof value === 'string' && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());
}

function storage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (_) {
    return null;
  }
}

/**
 * The stored colours, as a partial map. Never null, and never carries a key it should not.
 *
 * <p>Unknown keys and unparseable values are DROPPED rather than kept: this object is spread into a
 * chart option, and a key that survives from a future version of the dialog would be a colour
 * nothing can be set back from. Same for a malformed one — silently rendering black is worse than
 * rendering the default.
 */
export function readWorkspaceColors() {
  try {
    const raw = storage()?.getItem(WORKSPACE_COLORS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out = {};
    WORKSPACE_COLOR_KEYS.forEach((key) => {
      if (isHexColor(parsed[key])) out[key] = String(parsed[key]).trim().toLowerCase();
    });
    return out;
  } catch (_) {
    // A corrupt entry reads as unset. Throwing here would take the chart down over a preference.
    return {};
  }
}

/**
 * Stores a partial map and announces it. Returns what is now in force.
 *
 * <p>A key set to null, undefined or anything unparseable is REMOVED, which is how the dialog's
 * "reset to default" works without needing a second function: there is no such thing as a stored
 * default, only the absence of a choice.
 */
export function writeWorkspaceColors(next) {
  const merged = { ...readWorkspaceColors() };
  Object.keys(next || {}).forEach((key) => {
    if (!WORKSPACE_COLOR_KEYS.includes(key)) return;
    if (isHexColor(next[key])) merged[key] = String(next[key]).trim().toLowerCase();
    else delete merged[key];
  });
  try {
    const s = storage();
    if (s) {
      if (Object.keys(merged).length === 0) s.removeItem(WORKSPACE_COLORS_KEY);
      else s.setItem(WORKSPACE_COLORS_KEY, JSON.stringify(merged));
    }
  } catch (_) {
    // Storage full or blocked. The value still takes effect for this session via the event below,
    // which is the half a customer notices; losing it on reload beats refusing the change.
  }
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent(WORKSPACE_COLORS_CHANGED_EVENT, { detail: merged }));
    }
  } catch (_) { /* no window: nothing is listening either */ }
  return merged;
}

/** Clears every choice. */
export function clearWorkspaceColors() {
  return writeWorkspaceColors(
    WORKSPACE_COLOR_KEYS.reduce((acc, k) => ({ ...acc, [k]: null }), {}));
}

/**
 * The colour to draw with: the customer's choice, else the caller's own default.
 *
 * <p>The fallback is a parameter rather than a lookup so this module never becomes a second home
 * for a default. It is also what makes the feature additive: every call site passes the exact
 * expression it used before, so an unconfigured install is unchanged.
 */
export function resolveColor(colors, role, fallback) {
  const chosen = colors && colors[role];
  return isHexColor(chosen) ? chosen : fallback;
}

/**
 * Subscribes to changes from this tab or another one. Returns an unsubscribe.
 *
 * <p>The same two listeners `onBarModeChange` uses, for the same reason: a `CustomEvent` for this
 * tab, because `storage` fires only in the others, and `storage` so a second window agrees. A
 * customer with the chart open twice would otherwise see one of them keep the old palette.
 */
export function onWorkspaceColorsChange(onChange) {
  const local = e => onChange(e && 'detail' in e ? e.detail : readWorkspaceColors());
  const remote = (e) => {
    if (!e || e.key === null || e.key === WORKSPACE_COLORS_KEY) onChange(readWorkspaceColors());
  };
  window.addEventListener(WORKSPACE_COLORS_CHANGED_EVENT, local);
  window.addEventListener('storage', remote);
  return () => {
    window.removeEventListener(WORKSPACE_COLORS_CHANGED_EVENT, local);
    window.removeEventListener('storage', remote);
  };
}
